import {
  differenceInMonths,
  differenceInYears,
  format,
  getYear,
  isBefore,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

import { MAX_AGE_YEARS } from '~shared/constants/age';

export const convertToUTCDate = (date: Date) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // console.log('🚀 ~ convertToUTCDate ~ userTimeZone:', userTimeZone);
  const dateTime = fromZonedTime(date, userTimeZone);
  return dateTime;
};

export const normalizeAndConvertToUTCDate = (date: Date) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // console.log(
  //   '🚀 ~ normalizeAndConvertToUTCDate ~ userTimeZone:',
  //   userTimeZone,
  // );
  const normalizedDate = startOfDay(date);
  const dateTime = fromZonedTime(normalizedDate, userTimeZone);
  return dateTime;
};

// Calculate age in years
export const calculateAge = (date: string) => {
  const currentDate = new Date();
  const birthDate = parseISO(date);
  const age = differenceInYears(currentDate, birthDate);

  return age;
};

export const getSession = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) {
    return '';
  }
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const startYear = getYear(start);
  const endYear = getYear(end);
  if (startYear === endYear) {
    return startYear;
  } else {
    return `${startYear} - ${endYear}`;
  }
};

/**
 * Calculates the age in years and months from a UTC date string.
 *
 * @param dateString - The UTC date string to calculate the age from.
 * The expected format is "EEE, dd MMM yyyy HH:mm:ss 'GMT'".
 * @returns An object containing the age in years and months.
 * Returns { years: 0, months: 0 } if the input date string is empty or null.
 *
 * @example
 * ```typescript
 * const age = calculateAgeFromUTC("Mon, 01 Jan 1990 00:00:00 GMT");
 * console.log(age); // Output: { years: 34, months: 3 } (as of April 2024)
 * ```
 */
export function calculateAgeFromUTC(dateString: string) {
  if (!dateString) {
    return { years: 0, months: 0 };
  }
  // Parse the date string into a Date object
  const birthDate = parse(
    dateString,
    "EEE, dd MMM yyyy HH:mm:ss 'GMT'",
    new Date(),
  );

  // Get the current date
  const currentDate = new Date();

  // Calculate the age in years
  let years = differenceInYears(currentDate, birthDate);

  // Adjust years if the current date is before the birthday this year
  const birthdayThisYear = new Date(
    currentDate.getFullYear(),
    birthDate.getMonth(),
    birthDate.getDate(),
  );

  if (isBefore(currentDate, birthdayThisYear)) {
    years -= 1;
  }

  // Calculate the total difference in months
  const totalMonths = differenceInMonths(currentDate, birthDate);

  // Get the remaining months after the full years
  const months = totalMonths % 12;

  return { years, months };
}

/**
 * Calculates the age in years and months from an ISO date string.
 *
 * @param dateString - The ISO date string representing the birth date.
 * @returns An object containing the calculated years and months.
 *          Returns { years: 0, months: 0 } if the input date string is empty.
 */
export function calculateAgeFromISO(dateString: string): {
  years: number;
  months: number;
} {
  if (!dateString) {
    return { years: 0, months: 0 };
  }

  // Parse the ISO date string into a Date object
  const birthDate = parseISO(dateString);

  // Get the current date
  const currentDate = new Date();

  // Calculate the age in years
  const years = differenceInYears(currentDate, birthDate);

  // Calculate the total difference in months
  const totalMonths = differenceInMonths(currentDate, birthDate);

  // Get the remaining months after the full years
  const months = totalMonths % 12;

  return { years, months };
}

/**
 * Returns true only when the value is present and parses to a valid date.
 *
 * @param value - Date value (ISO string, Date, timestamp, or nullish).
 */
export function isValidDateValue(
  value: string | number | Date | null | undefined,
): boolean {
  if (value === null || value === undefined || value === '') return false;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date);
}

/**
 * Safely formats a date value, returning a fallback for missing/invalid input.
 *
 * @param value - Date value (ISO string, Date, timestamp, or nullish).
 * @param dateFormat - date-fns format string.
 * @param fallback - Returned when the value is missing or not a valid date.
 */
export function formatDateSafe(
  value: string | number | Date | null | undefined,
  dateFormat: string,
  fallback = '-',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (!isValid(date)) return fallback;
  return format(date, dateFormat);
}

/**
 * Formats a date-only value (DOB, etc.) using the UTC/calendar day, not local TZ.
 * UTC-midnight ISO strings otherwise render one day early in US timezones (ENR-3886).
 */
export function formatDateOnlySafe(
  value: string | number | Date | null | undefined,
  dateFormat: string,
  fallback = '-',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const noon = parseToNoonDate(
    value instanceof Date || typeof value === 'string' ? value : String(value),
  );
  if (!noon || !isValid(noon)) return fallback;
  return format(noon, dateFormat);
}

/**
 * Parses a date and time string and formats it into a time string.
 *
 * @param dateTimeString - The date and time string to parse.
 * @returns A formatted time string in the format "hh:mm a", or "Invalid date" if the input is invalid.
 */
export function parseAndFormatTime(dateTimeString: string): string {
  const date = parseISO(dateTimeString);
  if (date.toString() === 'Invalid Date') {
    return 'Invalid date';
  }
  return format(date, 'hh:mm a');
}

/**
 * Parses a time string in the format "HH:MM" and returns a Date object representing that time on the current day.
 *
 * @param {string} timeString - The time string to parse, in "HH:MM" format.
 * @returns {Date} A Date object representing the parsed time on the current day.
 * @throws {Error} If the timeString is not in the correct format.
 */
export const parseTimeString = (timeString: string): Date => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const now = new Date();
  const time = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    0,
    0,
  );
  return time;
};

/**
 * Safely format a class time value (`HH:mm` or full ISO string) for display.
 * Returns null if the input is missing, malformed, or produces an Invalid Date.
 *
 * Use this anywhere we render `enrollmentId.startTime` / `endTime` so a corrupted
 * or unpopulated class reference (e.g. orphaned enrollments) cannot throw
 * `RangeError: Invalid time value` from `date-fns#format` and crash the page.
 *
 * @example
 * formatClassTime('09:00');                      // '09:00 AM'
 * formatClassTime('2025-02-16T07:30:00.000Z');   // '07:30 AM' (in UTC)
 * formatClassTime('');                           // null
 * formatClassTime(undefined);                    // null
 * formatClassTime('not-a-date');                 // null
 */
export function formatClassTime(
  timeString: string | null | undefined,
  pattern = 'hh:mm a',
): string | null {
  if (timeString == null || timeString === '') return null;

  const time = timeString.includes('T')
    ? new Date(timeString)
    : parseTimeString(timeString);

  if (!(time instanceof Date) || Number.isNaN(time.getTime())) return null;

  try {
    return format(time, pattern);
  } catch {
    return null;
  }
}

/** Coerce API values to an ISO-like string (handles runtime `Date` from Mongo/transforms). */
function toIsoDateString(input: string | Date | null | undefined): string {
  if (input == null || input === '') return '';
  if (typeof input === 'string') return input;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? '' : input.toISOString();
  }
  return String(input);
}

// Function to extract just the date part from an ISO string or any date string
export const extractDateOnly = (input: string | Date | null | undefined) => {
  const dateString = toIsoDateString(input);
  if (!dateString) return '';

  // If it's an ISO string with timezone (from backend)
  if (dateString.includes('T')) {
    // Extract just the YYYY-MM-DD part
    return dateString.split('T')[0];
  }

  // If it's already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // For other formats, create a date and extract components
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Formats a class or event age range for display, hiding the sentinel values
 * studios end up with when they leave a bound unset (`0y 0m`) or push the
 * maximum to the system limit.
 *
 * @example
 * formatAgeRange(0, 0, 0, 0)    // "All Ages"
 * formatAgeRange(3, 0, 18, 0)   // "3y - 18y"
 * formatAgeRange(0, 6, 8, 0)    // "Up to 8y"
 * formatAgeRange(5, 0, 100, 0)  // "5y+"
 */
export function formatAgeRange(
  startYear?: number | null,
  startMonth?: number | null,
  endYear?: number | null,
  endMonth?: number | null,
): string {
  const start = { year: startYear ?? 0, month: startMonth ?? 0 };
  const end = { year: endYear ?? 0, month: endMonth ?? 0 };

  const noStart = start.year === 0 && start.month === 0;
  const noEnd =
    (end.year === 0 && end.month === 0) || end.year >= MAX_AGE_YEARS;

  if (noStart && noEnd) return 'All Ages';

  const formatAge = (year: number, month: number) =>
    month === 0 ? `${year}y` : `${year}y ${month}m`;

  if (noStart) return `Up to ${formatAge(end.year, end.month)}`;
  if (noEnd) return `${formatAge(start.year, start.month)}+`;
  return `${formatAge(start.year, start.month)} - ${formatAge(end.year, end.month)}`;
}

/**
 * Formats a single age bound, returning "None" for the sentinel values that
 * mean "no bound configured".
 */
export function formatAgeBound(
  year?: number | null,
  month?: number | null,
): string {
  const years = year ?? 0;
  const months = month ?? 0;
  if ((years === 0 && months === 0) || years >= MAX_AGE_YEARS) return 'None';
  return months === 0 ? `${years}y` : `${years}y ${months}m`;
}

/**
 * Formats age in years and months into a human-readable string.
 *
 * @param years - The number of years.
 * @param months - The number of months.
 * @returns A formatted string like "5 years 3 months" or "1 year 1 month".
 *
 * @example
 * ```typescript
 * formatAgeString(5, 3); // "5 years 3 months"
 * formatAgeString(1, 1); // "1 year 1 month"
 * formatAgeString(0, 6); // "0 years 6 months"
 * ```
 */
export function formatAgeString(years: number, months: number): string {
  const yearText = years > 1 ? 'years' : 'year';
  const monthText = months > 1 ? 'months' : 'month';
  return `${years} ${yearText} ${months} ${monthText}`;
}

/**
 * Parses a date string and returns a Date object with time set to noon to avoid timezone issues.
 * Returns null if the input is empty or invalid.
 *
 * @param dateInput - ISO string or Date (strings are typical from APIs; Date can appear after transforms)
 * @returns A Date object with time set to 12:00:00, or null if input is empty/invalid
 *
 * @example
 * parseToNoonDate("2024-01-15T00:00:00Z"); // Date: 2024-01-15 12:00:00
 * parseToNoonDate("2024-01-15"); // Date: 2024-01-15 12:00:00
 * parseToNoonDate(""); // null
 * parseToNoonDate(null); // null
 */
export function parseToNoonDate(
  dateInput: string | Date | null | undefined,
): Date | null {
  if (dateInput == null || dateInput === '') return null;
  if (dateInput instanceof Date && Number.isNaN(dateInput.getTime()))
    return null;
  const extracted = extractDateOnly(dateInput);
  if (!extracted) return null;
  return new Date(`${extracted}T12:00:00`);
}

/**
 * Format date as YYYY-MM-DD in local timezone
 * @param date The date to format
 * @returns A string in YYYY-MM-DD format
 */
export function formatLocalDate(date: Date | null | undefined): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format date as ISO string preserving the local date (for date-only fields)
 * This prevents timezone conversion from shifting the date backwards
 * @param date The date to format
 * @returns An ISO string in format YYYY-MM-DDTHH:mm:ss.sssZ with time set to midnight UTC
 * @example
 * If user selects 2025-09-26 in IST (UTC+5:30)
 * Returns: "2025-09-26T00:00:00.000Z" (not "2025-09-25T18:30:00.000Z")
 */
export function formatDateAsISO(date: Date | null | undefined): string {
  if (!date) return new Date().toISOString();
  const localDateStr = formatLocalDate(date);
  return `${localDateStr}T00:00:00.000Z`;
}

/**
 * Serializes a date-only field to a `YYYY-MM-DD` string for API submission.
 * `Date` inputs are serialized by their *local* calendar day (the day a
 * date-picker displayed); string inputs are trimmed to their date-only part.
 * Returns `''` for empty/invalid input.
 */
export const toDateOnlyString = (
  value: string | Date | null | undefined,
): string => {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    return formatLocalDate(value);
  }
  return extractDateOnly(value) ?? '';
};

/**
 * Extract the calendar date (YYYY-MM-DD) from an API date string.
 * Use for date-only fields (class/session start/end, billing date) so
 * display is not affected by timezone. Handles ISO (e.g. 2026-06-15T00:00:00.000Z)
 * and plain YYYY-MM-DD.
 */
const API_DATE_FORMATS = [
  'yyyy-MM-dd',
  'yyyy/MM/dd',
  'dd/MM/yyyy',
  'MM/dd/yyyy',
  'yyyy-MM-dd HH:mm:ss',
  'yyyy-MM-dd HH:mm:ss.SSS',
] as const;

export function extractDateOnlyFromAPI(dateString: string): string {
  if (!dateString) return '';
  if (dateString.includes('T')) return dateString.split('T')[0] ?? '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  const ref = new Date(0); // arbitrary reference for parse
  for (const fmt of API_DATE_FORMATS) {
    const d = parse(dateString, fmt, ref);
    if (isValid(d)) {
      return format(d, 'yyyy-MM-dd');
    }
  }
  return '';
}

/**
 * Format an API date string for display (e.g. "Jun 15, 2026").
 * Uses only the calendar date — not affected by timezone or browser.
 * Use for class dates, session dates, billing date, etc. so the same
 * date (e.g. Jun 15, 2026) shows everywhere regardless of location.
 */
export function formatDateForDisplay(dateString: string): string {
  const dateOnly = extractDateOnlyFromAPI(dateString);
  if (!dateOnly) return '';
  const date = parse(dateOnly, 'yyyy-MM-dd', new Date());
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'MMM d, yyyy');
}

// Function to format ISO string to MM/dd/yyyy, truly ignoring timezone
export const formatDateIgnoreTimezone = (isoString: string) => {
  const datePart = extractDateOnlyFromAPI(isoString);

  if (!datePart) {
    return 'Invalid date';
  }
  const date = parse(datePart, 'yyyy-MM-dd', new Date());
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  return format(date, 'MM/dd/yyyy');
};
